/**
 * By-bucket credit-grant editor (v2) — the ONE shared editor for a plan's per-bucket
 * credit **deposits** (DNKADM-0056 U1) AND a package's per-bucket credit **grants**
 * (DNKADM-0102 U2). Both are the same `(entity, creditBucketId) → amount` upsert shape
 * (DNKAPI-0178 / DNKAPI-0181), so ONE component drives both — the caller passes the
 * owning `entityId` + the three get/set/delete callbacks (never a forked second copy).
 *
 * It lists the entity's grants (bucket name + amount), and offers the active credit
 * buckets (the 0052 fail-closed pick-list) NOT already granted, so a duplicate
 * `(entity, bucket)` cannot be entered. Add upserts via `setCredit`; a row's Remove
 * deletes it via `deleteCredit`. Keyed by **ids** throughout (the bucket id, not a
 * string) — mirrors the 0055 per-staff price editor, not the v1 tier/bucket matrix.
 *
 * The user-facing copy defaults to the Plans wording (so the Plans path is byte-for-byte
 * unchanged); Packages passes a `copy` override for grant-centric wording. An optional
 * `onGrantsChange` reports the current grant list after each (re)load so a parent (the
 * package dialog) can warn on a zero-grant offering. Callbacks MUST be stable references
 * (module functions or `useCallback`) — they are effect deps.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { IconAction } from '@/components/IconAction';
import { Icon } from '@/theme';
import { useCreditBucketPickList } from '@/api/creditBuckets';
import { PickListLoader } from '@/components/PickListLoader';
import { formatCredits } from '@/utils/v2Format';
import { serverMessage } from '@/api/serverMessage';
import type { ShowToast } from '@/components/toastContext';

/**
 * One by-bucket grant row (a `PlanCredit` / `OfferingCredit` seen through the shared
 * lens): keyed by `creditBucketId`, carrying its `amount`. Extra fields on the concrete
 * types (e.g. an offering credit's `bucketName`) are ignored here — the editor resolves
 * the display name from the live pick-list.
 */
export interface CreditGrant {
  creditBucketId: string;
  amount: number;
  /**
   * DNKADM-0127: per-credit expiry for offerings — `0`/undefined = never expires,
   * `N` (> 0) = the deposited credits expire `N` days after purchase. Offerings carry
   * it (via `showExpiry`); plan deposits leave it undefined (never surfaced).
   */
  expiryDays?: number;
}

/** The user-facing copy — defaults to the Plans wording; Packages overrides it. */
export interface CreditGrantsEditorCopy {
  heading: string;
  hint: string;
  loadError: string;
  tableAriaLabel: string;
  /** Aria label for a row's Remove button, given the bucket's display name. */
  removeAriaLabel: (bucketName: string) => string;
  addButton: string;
  savedToast: string;
  removedToast: string;
  saveError: string;
  removeError: string;
  chooseBucketError: string;
  amountError: string;
  /** Shown when the tenant has NO credit buckets yet (nothing to grant). */
  emptyBucketsInfo: string;
  /** Shown when every bucket already has a grant on this entity. */
  allUsedHint: string;
}

/** Default copy — the Plans "credit deposits" wording (keeps the Plans path identical). */
const DEFAULT_COPY: CreditGrantsEditorCopy = {
  heading: 'Credit deposits',
  hint: 'The credits this plan deposits in each bucket on purchase or renewal.',
  loadError: 'Couldn’t load the credit deposits.',
  tableAriaLabel: 'Plan credit deposits',
  removeAriaLabel: (name) => `Remove credit deposit for ${name}`,
  addButton: 'Add deposit',
  savedToast: 'Credit deposit saved',
  removedToast: 'Credit deposit removed',
  saveError: 'Couldn’t save the credit deposit.',
  removeError: 'Couldn’t remove the credit deposit.',
  chooseBucketError: 'Choose a credit bucket.',
  amountError: 'Enter an amount (0 or more).',
  emptyBucketsInfo: 'Create a credit bucket first to set this plan’s credit deposits.',
  allUsedHint: 'Every credit bucket already has a deposit on this plan.',
};

export interface PlanCreditsEditorProps {
  /** The owning entity id (a plan id, or a package/offering id). */
  entityId: string;
  /** Load the entity's current by-bucket grants (a stable reference). */
  loadCredits: (entityId: string) => Promise<CreditGrant[]>;
  /**
   * Upsert one `(bucket, amount)` grant on the entity (a stable reference). The 4th
   * `expiryDays` arg is sent only when {@link PlanCreditsEditorProps.showExpiry} is on
   * (offerings); a 3-arg plan-deposit callback stays assignable and ignores it.
   */
  setCredit: (
    entityId: string,
    creditBucketId: string,
    amount: number,
    expiryDays?: number,
  ) => Promise<void>;
  /** Remove a grant from the entity (a stable reference). */
  deleteCredit: (entityId: string, creditBucketId: string) => Promise<void>;
  showToast: ShowToast;
  /** Optional copy override (Packages); defaults to the Plans wording. */
  copy?: CreditGrantsEditorCopy;
  /** Optional: reports the current grant list after each (re)load (for a zero-grant warning). */
  onGrantsChange?: (grants: CreditGrant[]) => void;
  /**
   * DNKADM-0127: opt in to the per-credit expiry control + column (offerings only;
   * default `false`). When off, no expiry UI renders and `setCredit` is called 3-arg —
   * the plan-deposit path stays byte-for-byte unchanged.
   */
  showExpiry?: boolean;
}

/** Parse a credit-amount text field to a finite number ≥ 0, or null when invalid/blank. */
function parseAmount(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * DNKADM-0127: parse an expiry-days field to a positive integer (≥ 1), or null when
 * invalid/blank (mirrors `PlanCreditGrantsEditor`).
 */
function parsePositiveInt(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && Number.isInteger(n) && n >= 1 ? n : null;
}

/** The expiry Days validation error (DNKADM-0127; mirrors the plan add-on wording). */
const EXPIRY_DAYS_ERROR = 'Enter a whole number of days, 1 or more.';

export function PlanCreditsEditor({
  entityId,
  loadCredits,
  setCredit,
  deleteCredit,
  showToast,
  copy = DEFAULT_COPY,
  onGrantsChange,
  showExpiry = false,
}: PlanCreditsEditorProps) {
  const ids = useId();
  const buckets = useCreditBucketPickList();

  const [rows, setRows] = useState<CreditGrant[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [newBucketId, setNewBucketId] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // DNKADM-0127: per-credit expiry add-row state (only rendered/used when showExpiry).
  const [expiryMode, setExpiryMode] = useState<'never' | 'after'>('never');
  const [expiryDays, setExpiryDays] = useState('');

  // Keep the change-reporter current without making it an effect dependency.
  const onGrantsChangeRef = useRef(onGrantsChange);
  useEffect(() => {
    onGrantsChangeRef.current = onGrantsChange;
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await loadCredits(entityId);
        if (cancelled) return;
        setRows(loaded);
        setLoadError(false);
        onGrantsChangeRef.current?.(loaded);
      } catch {
        if (cancelled) return;
        setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entityId, reloadKey, loadCredits]);

  const bucketItems = buckets.items;
  const bucketOptions = useMemo(() => bucketItems ?? [], [bucketItems]);
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of bucketOptions) map.set(b.id, b.name || b.id);
    return map;
  }, [bucketOptions]);

  // Only buckets WITHOUT an existing grant are addable (unique (entity, bucket)).
  const granted = useMemo(() => new Set(rows.map((r) => r.creditBucketId)), [rows]);
  const addableBuckets = useMemo(
    () => bucketOptions.filter((b) => !granted.has(b.id)),
    [bucketOptions, granted],
  );

  async function handleAdd() {
    setAddError(null);
    if (newBucketId === '') {
      setAddError(copy.chooseBucketError);
      return;
    }
    const amount = parseAmount(newAmount);
    if (amount === null) {
      setAddError(copy.amountError);
      return;
    }
    // DNKADM-0127: expiry is opt-in — resolve the days only when the control is shown
    // and set to "after"; otherwise the plan-deposit call stays 3-arg (unchanged).
    let expiry = 0;
    if (showExpiry && expiryMode === 'after') {
      const n = parsePositiveInt(expiryDays);
      if (n === null) {
        setAddError(EXPIRY_DAYS_ERROR);
        return;
      }
      expiry = n;
    }
    setBusy(true);
    try {
      if (showExpiry) {
        await setCredit(entityId, newBucketId, amount, expiry);
      } else {
        await setCredit(entityId, newBucketId, amount);
      }
      setNewBucketId('');
      setNewAmount('');
      if (showExpiry) {
        setExpiryMode('never');
        setExpiryDays('');
      }
      setReloadKey((k) => k + 1);
      showToast(copy.savedToast, 'success');
    } catch (err) {
      setAddError(serverMessage(err) ?? copy.saveError);
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(creditBucketId: string) {
    setBusy(true);
    try {
      await deleteCredit(entityId, creditBucketId);
      setReloadKey((k) => k + 1);
      showToast(copy.removedToast, 'success');
    } catch (err) {
      showToast(serverMessage(err) ?? copy.removeError, 'error');
    } finally {
      setBusy(false);
    }
  }

  const bucketLabelId = `${ids}-bucket-label`;
  const expiryLabelId = `${ids}-expiry-label`;

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {copy.heading}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        {copy.hint}
      </Typography>

      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 1, py: 0.5 }}
          action={
            <Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>
              Retry
            </Button>
          }
        >
          {copy.loadError}
        </Alert>
      )}

      {rows.length > 0 && (
        <Table size="small" aria-label={copy.tableAriaLabel} sx={{ mb: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>Credit bucket</TableCell>
              <TableCell align="right">Amount</TableCell>
              {showExpiry && <TableCell>Expiry</TableCell>}
              <TableCell align="right">Remove</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.creditBucketId}>
                <TableCell>{nameById.get(row.creditBucketId) ?? row.creditBucketId}</TableCell>
                <TableCell align="right">{formatCredits(row.amount)}</TableCell>
                {showExpiry && (
                  <TableCell>
                    {(row.expiryDays ?? 0) > 0
                      ? `Expires ${row.expiryDays} days after purchase`
                      : 'Never expires'}
                  </TableCell>
                )}
                <TableCell align="right">
                  <IconAction
                    size="small"
                    label={copy.removeAriaLabel(nameById.get(row.creditBucketId) ?? row.creditBucketId)}
                    disabled={busy}
                    onClick={() => void handleRemove(row.creditBucketId)}
                  >
                    <Icon name="delete" />
                  </IconAction>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <PickListLoader list={buckets} label="credit buckets" loadingVariant="inline">
        {(options) =>
          options.length === 0 ? (
            <Alert severity="info" sx={{ py: 0.5 }}>
              {copy.emptyBucketsInfo}
            </Alert>
          ) : (
            <>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{ alignItems: 'flex-start' }}
              >
                <FormControl
                  size="small"
                  sx={{ minWidth: 200 }}
                  disabled={busy || addableBuckets.length === 0}
                >
                  <InputLabel id={bucketLabelId}>Credit bucket</InputLabel>
                  <Select
                    labelId={bucketLabelId}
                    label="Credit bucket"
                    value={newBucketId}
                    onChange={(e) => setNewBucketId(e.target.value)}
                    slotProps={{ input: { 'aria-label': 'Credit bucket' } }}
                  >
                    {addableBuckets.map((b) => (
                      <MenuItem key={b.id} value={b.id}>
                        {b.name || b.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Amount"
                  type="number"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                  disabled={busy || addableBuckets.length === 0}
                  size="small"
                  slotProps={{
                    htmlInput: {
                      'aria-label': 'Credit amount',
                      min: 0,
                      step: '0.01',
                      inputMode: 'decimal',
                    },
                  }}
                />
                {/* DNKADM-0127: per-credit expiry (offerings only) — mirrors the plan
                    add-on Select + Days field; absent entirely for plan deposits. */}
                {showExpiry && (
                  <>
                    <FormControl
                      size="small"
                      sx={{ minWidth: 200 }}
                      disabled={busy || addableBuckets.length === 0}
                    >
                      <InputLabel id={expiryLabelId}>Expiry</InputLabel>
                      <Select
                        labelId={expiryLabelId}
                        label="Expiry"
                        value={expiryMode}
                        onChange={(e) => setExpiryMode(e.target.value as 'never' | 'after')}
                        slotProps={{ input: { 'aria-label': 'Expiry' } }}
                      >
                        <MenuItem value="never">Never expires</MenuItem>
                        <MenuItem value="after">Expires N days after purchase</MenuItem>
                      </Select>
                    </FormControl>
                    {expiryMode === 'after' && (
                      <TextField
                        label="Days"
                        type="number"
                        value={expiryDays}
                        onChange={(e) => setExpiryDays(e.target.value)}
                        disabled={busy || addableBuckets.length === 0}
                        size="small"
                        slotProps={{
                          htmlInput: {
                            'aria-label': 'Days until expiry',
                            min: 1,
                            step: 1,
                            inputMode: 'numeric',
                          },
                        }}
                      />
                    )}
                  </>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => void handleAdd()}
                  disabled={busy || addableBuckets.length === 0}
                  sx={{ mt: 0.5 }}
                >
                  {copy.addButton}
                </Button>
              </Stack>
              {addableBuckets.length === 0 && (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
                >
                  {copy.allUsedHint}
                </Typography>
              )}
            </>
          )
        }
      </PickListLoader>

      {addError && (
        <Alert severity="error" role="alert" sx={{ mt: 1, py: 0.5 }}>
          {addError}
        </Alert>
      )}
    </Box>
  );
}
